/* eslint-disable camelcase */
import { SelectInput, Button, useForm, ISelectInputOption } from '@rocketmakers/armstrong';
import * as React from 'react';
import { generateSelectOptions, enumToArray } from '../../../../shared/helpers/enumHelpers';
import { useValidatedForm } from '../../../../shared/hooks/formValidationHook';
import { InputValidator } from '../../../../shared/hooks/ValidationTypes';
import { requiredTextInputValidator } from '../../../../shared/hooks/Validators';
import { BackButton } from '../../components/buttons/back-button';
import { YesNoOption } from '../../../../shared/enums/yesNoOption';
import { hasCompleteParams, useBeeConfig } from '../../hooks/beeConfig';

import { showNextOptionValidator } from './validators/validators';
import { useRoutePaths } from '../../../../shared/context/paths';
import { routePaths } from '../../routes';
import {
  HotWaterSourceMasterTherm,
  HotWaterSourceRelay,
  DeviceType,
  HotWaterSource,
  HotWaterSourceType,
  RuleCategory,
  RuleActionType,
  RuleVariant,
  DeviceInfo,
} from '../../../../shared/api-clients/_api';
import { SelectAvailableRelay } from '../../components/select-available-relay';
import { ConfirmAction } from '../../components/confirm-action/confirm-action';
import { Spinner } from '../../components/spinner/spinner';

type IHotWaterSourceForm = {
  hotWaterSource: string;
  hotWaterSourceRelayAddress: number;
  isImmersionHeater?: string;
  immersionHeaterRelayAddress?: number;
};

const initialState: IHotWaterSourceForm = {
  hotWaterSource: '',
  hotWaterSourceRelayAddress: null,
  isImmersionHeater: '',
  immersionHeaterRelayAddress: null,
};

const validators = (
  showPrimaryRelayOption: boolean,
  showImmersionOption: boolean,
  immersionHeaterOptionSelected: boolean
): InputValidator<IHotWaterSourceForm>[] => {
  return [
    {
      key: 'hotWaterSource',
      validators: [requiredTextInputValidator],
    },
    {
      key: 'hotWaterSourceRelayAddress',
      validators: [showNextOptionValidator(showPrimaryRelayOption)],
    },
    {
      key: 'isImmersionHeater',
      validators: [showNextOptionValidator(showImmersionOption)],
    },
    {
      key: 'immersionHeaterRelayAddress',
      validators: [showNextOptionValidator(immersionHeaterOptionSelected)],
    },
  ];
};

const primaryRelayHotWaterSources = enumToArray(HotWaterSource).filter(
  source => source === HotWaterSource.SYSTEM_BOILER || source === HotWaterSource.IMMERSION_HEATER
);

const immersionHotWaterSources = enumToArray(HotWaterSource).filter(
  source => source === HotWaterSource.HEAT_PUMP || source === HotWaterSource.SYSTEM_BOILER
);

const determineDeviceTypeAndRuleToUseForPrimaryHotWaterSource = (
  hotWaterSource: string
): HotWaterSourceMasterTherm | HotWaterSourceRelay => {
  const options = {
    [HotWaterSource.IMMERSION_HEATER]: {
      deviceType: DeviceType.RELAY,
      params: { variant: RuleVariant.IMMERSION_HEATER },
    },
    [HotWaterSource.SYSTEM_BOILER]: {
      deviceType: DeviceType.RELAY,
      params: { variant: RuleVariant.SYSTEM_BOILER },
    },
    [HotWaterSource.COMBI_BOILER]: {
      deviceType: undefined,
      params: { variant: RuleVariant.COMBI_BOILER },
    },
    [HotWaterSource.OTHER]: {
      deviceType: undefined,
      params: { variant: undefined },
    },
    [HotWaterSource.HEAT_PUMP]: {
      deviceType: DeviceType.MASTERTHERM,
      params: { variant: undefined },
    },
  };

  return options[hotWaterSource];
};

export const HotWaterView: React.FunctionComponent = () => {
  const { push } = useRoutePaths();

  const { devices, getDevicesByCategory, pushDevicesToConfig, addDevicesToConfig, removeDevicesFromConfig, loading } =
    useBeeConfig();

  const [confirmationBoxTicked, setConfirmationBoxTicked] = React.useState(false);

  const currentHotWaterConfig = React.useMemo(() => {
    const primaryHotWaterSourceOptions = {
      [RuleVariant.COMBI_BOILER]: HotWaterSource.COMBI_BOILER,
      [RuleVariant.SYSTEM_BOILER]: HotWaterSource.SYSTEM_BOILER,
      [RuleVariant.IMMERSION_HEATER]: HotWaterSource.IMMERSION_HEATER,
      [DeviceType.MASTERTHERM]: HotWaterSource.HEAT_PUMP,
    };

    const currentHotWaterDevices = getDevicesByCategory<HotWaterSourceRelay | HotWaterSourceMasterTherm>(
      RuleCategory.HOT_WATER
    );

    const currentPrimaryHotWaterDevice = currentHotWaterDevices.find(
      ({ params: { hot_water_source_type } }) => hot_water_source_type === HotWaterSourceType.PRIMARY
    );

    if (!currentPrimaryHotWaterDevice) return;

    const currentSecondaryHotWaterDevice = currentHotWaterDevices.find(
      ({ params: { hot_water_source_type } }) => hot_water_source_type === HotWaterSourceType.SECONDARY
    );

    const lookupKey = currentPrimaryHotWaterDevice.params.variant ?? currentPrimaryHotWaterDevice.deviceType;
    const primaryHotWaterSource = primaryHotWaterSourceOptions[lookupKey] ?? HotWaterSource.OTHER;
    const primaryHotWaterSourceRelayAddress = currentPrimaryHotWaterDevice.params.address;
    const immersionHeaterSelectedOption = currentSecondaryHotWaterDevice ? YesNoOption.YES : YesNoOption.NO;
    const immersionHeaterRelayAddress = currentSecondaryHotWaterDevice?.params.address;

    if (primaryHotWaterSource === HotWaterSource.HEAT_PUMP) setConfirmationBoxTicked(true);

    return {
      currentHotWaterDevices,
      primaryHotWaterSource,
      primaryHotWaterSourceRelayAddress,
      immersionHeaterSelectedOption,
      immersionHeaterRelayAddress,
    };
  }, [devices]);

  const initState: IHotWaterSourceForm = React.useMemo(() => {
    return currentHotWaterConfig
      ? {
          hotWaterSource: currentHotWaterConfig.primaryHotWaterSource,
          hotWaterSourceRelayAddress:
            currentHotWaterConfig.primaryHotWaterSourceRelayAddress.toString() === '0'
              ? null
              : currentHotWaterConfig.primaryHotWaterSourceRelayAddress,
          isImmersionHeater:
            currentHotWaterConfig.primaryHotWaterSource === HotWaterSource.HEAT_PUMP ||
            currentHotWaterConfig.primaryHotWaterSource === HotWaterSource.SYSTEM_BOILER
              ? currentHotWaterConfig.immersionHeaterSelectedOption
              : '',
          immersionHeaterRelayAddress:
            currentHotWaterConfig.immersionHeaterSelectedOption === YesNoOption.YES
              ? currentHotWaterConfig.immersionHeaterRelayAddress
              : null,
        }
      : initialState;
  }, [currentHotWaterConfig]);

  const initialRelayValues = [
    currentHotWaterConfig?.primaryHotWaterSourceRelayAddress,
    currentHotWaterConfig?.immersionHeaterRelayAddress,
  ];

  const { DataForm, bind, dataBinder } = useForm(initState);

  const hotWaterSource: HotWaterSource = dataBinder.getValue('hotWaterSource');
  const isImmersionHeater: YesNoOption = dataBinder.getValue('isImmersionHeater');

  const showPrimaryRelayOption = React.useMemo(() => {
    return primaryRelayHotWaterSources.includes(hotWaterSource);
  }, [hotWaterSource]);

  const showImmersionOption = React.useMemo(() => {
    return immersionHotWaterSources.includes(hotWaterSource);
  }, [hotWaterSource]);

  const immersionHeaterOptionSelected = React.useMemo(() => isImmersionHeater === YesNoOption.YES, [isImmersionHeater]);

  const { setTouched, formIsValid, validationErrors } = useValidatedForm<IHotWaterSourceForm>(
    validators(showPrimaryRelayOption, showImmersionOption, immersionHeaterOptionSelected),
    dataBinder
  );

  const formOptions: Record<string, ISelectInputOption[]> = {
    hotWaterSource: generateSelectOptions(HotWaterSource),
    isImmersionHeater: generateSelectOptions(YesNoOption),
  };

  const getDevicesToAdd = (
    options: HotWaterSourceMasterTherm | HotWaterSourceRelay,
    hotWaterSourceRelayAddress: number
  ): DeviceInfo[] => {
    // Create rule for primary hot water source
    const devicesToAdd: DeviceInfo[] = [
      {
        ...options,
        params: {
          address: hotWaterSourceRelayAddress || 0,
          hot_water_source_type: HotWaterSourceType.PRIMARY,
          category: RuleCategory.HOT_WATER,
          action_type: RuleActionType.CONTROL,
          ...(options.params.variant ? { variant: options.params.variant } : {}),
        },
      },
    ];

    if (immersionHeaterOptionSelected && options.deviceType && hotWaterSource !== HotWaterSource.IMMERSION_HEATER) {
      const immersionHeaterRelayAddress = dataBinder.getValue('immersionHeaterRelayAddress');

      // Create second rule for secondary hot water source
      const immersionHeaterDevice: HotWaterSourceRelay = {
        deviceType: DeviceType.RELAY,
        params: {
          address: immersionHeaterRelayAddress,
          hot_water_source_type: HotWaterSourceType.SECONDARY,
          category: RuleCategory.HOT_WATER,
          action_type: RuleActionType.CONTROL,
          variant: RuleVariant.IMMERSION_HEATER,
        },
      };
      devicesToAdd.push(immersionHeaterDevice);
    }

    return devicesToAdd;
  };

  const handleCreate = async (
    options: HotWaterSourceMasterTherm | HotWaterSourceRelay,
    hotWaterSourceRelayAddress: number
  ) => {
    const devicesToAdd = getDevicesToAdd(options, hotWaterSourceRelayAddress);
    await addDevicesToConfig(...devicesToAdd);
  };

  const handleEdit = async (
    options: HotWaterSourceMasterTherm | HotWaterSourceRelay,
    hotWaterSourceRelayAddress: number
  ) => {
    const { currentHotWaterDevices } = currentHotWaterConfig;

    const devicesWithoutPrevConfig = devices
      .filter(hasCompleteParams)
      .filter(device => device.params.category !== RuleCategory.HOT_WATER);

    const devicesToAdd = getDevicesToAdd(options, hotWaterSourceRelayAddress);

    if (!options.deviceType) {
      await removeDevicesFromConfig(...currentHotWaterDevices);
    } else {
      await pushDevicesToConfig([...devicesToAdd, ...devicesWithoutPrevConfig]);
    }
  };

  const handleContinue = () => {
    const options = determineDeviceTypeAndRuleToUseForPrimaryHotWaterSource(hotWaterSource);
    const hotWaterSourceRelayAddress = dataBinder.getValue('hotWaterSourceRelayAddress');

    if (!options.deviceType && !currentHotWaterConfig) {
      return push(routePaths.verify.firmware.check);
    }

    if (currentHotWaterConfig) {
      handleEdit(options, hotWaterSourceRelayAddress);
    } else {
      handleCreate(options, hotWaterSourceRelayAddress);
    }

    push(routePaths.verify.firmware.check);
  };

  const renderPrimaryHotWaterSourceSelection = () => {
    return (
      <div>
        <SelectInput
          {...bind.select('hotWaterSource')}
          label="What is the primary hot water source installed at the property?"
          options={formOptions.hotWaterSource}
          onBlur={setTouched}
          data-qa="hot-water-source"
        />
      </div>
    );
  };

  const renderPrimaryHotWaterRelaySelection = () => {
    return hotWaterSource !== ('' as HotWaterSource) &&
      hotWaterSource !== HotWaterSource.OTHER &&
      hotWaterSource !== HotWaterSource.COMBI_BOILER &&
      hotWaterSource !== HotWaterSource.HEAT_PUMP ? (
      <div>
        <SelectAvailableRelay
          {...bind.select('hotWaterSourceRelayAddress')}
          label={'Which relay controls the primary hot water source?'}
          onBlur={setTouched}
          data-qa="hot-water-source-relay-address"
          groupInitialValues={initialRelayValues}
        />
      </div>
    ) : null;
  };

  const renderImmersionHeaterSelection = () => {
    return showImmersionOption ? (
      <div>
        <SelectInput
          {...bind.select('isImmersionHeater')}
          label="Is there also an immersion heater?"
          options={formOptions.isImmersionHeater}
          onBlur={setTouched}
          data-qa="immersion-heater"
        />
      </div>
    ) : null;
  };

  const renderRelaySelection = () => {
    return showImmersionOption && immersionHeaterOptionSelected ? (
      <div>
        <SelectAvailableRelay
          {...bind.select('immersionHeaterRelayAddress')}
          label="Which relay controls the immersion heater?"
          onBlur={setTouched}
          data-qa="immersion-heater-relay-address"
          groupInitialValues={initialRelayValues}
        />
      </div>
    ) : null;
  };

  const renderIPAddressConfirmationBox = () => {
    return hotWaterSource === HotWaterSource.HEAT_PUMP ? (
      <ConfirmAction
        actionConfirmed={confirmationBoxTicked}
        setActionConfirmed={() => setConfirmationBoxTicked(!confirmationBoxTicked)}
        message="I confirm I've fixed the heat pump IP address in the pGDX controller to 192.168.0.120"
      />
    ) : null;
  };

  return (
    <DataForm validationResults={validationErrors} validationMode="both">
      {loading && <Spinner />}
      <BackButton />
      <h1>
        Hot <span className="color-primary">water source</span>
      </h1>
      {renderPrimaryHotWaterSourceSelection()}

      {renderPrimaryHotWaterRelaySelection()}

      {renderImmersionHeaterSelection()}

      {renderRelaySelection()}

      {renderIPAddressConfirmationBox()}

      <div className="row">
        <Button
          className="continue-button btn-full btn-large"
          disabled={formIsValid === false || (!confirmationBoxTicked && hotWaterSource === HotWaterSource.HEAT_PUMP)}
          onClick={handleContinue}
          data-qa="continue-button"
        >
          Next
        </Button>
      </div>
    </DataForm>
  );
};
